import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Client } from '@core/interfaces/client.interface';
import { Page } from '@core/interfaces/page.interface';
import { Block } from '@core/models/block/block.model';
import { SpecialBlock } from '@core/models/block/special-block';
import { ApiBlockService } from '@core/services/api/api-block.service';
import { PageService } from '@core/services/page/page.service';
import { createTypesRecursive } from '@shared/services/factories/block-factory.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

/**
 * Menu i stopka jednej nawigacji. Strumienie są już uruchomione i pamiętają swój wynik,
 * więc można je subskrybować później (po aktywacji trasy) bez utraty odpowiedzi.
 * `null` oznacza, że blok nie ma być pokazany.
 */
export interface PageFrameBlocks {
  menu$: Observable<Block | null>;
  footer$: Observable<Block | null>;
}

/**
 * AT-9327: menu i stopka nie są potrzebne do wyrenderowania treści strony, a stopka
 * (404 przy braku stopki) potrafiła wstrzymać aktywację trasy o ~200 ms. Dlatego resolver
 * tylko je uruchamia, a strona dokleja je, gdy dojadą.
 */
@Injectable({providedIn: 'root'})
export class PageFrameBlocksService {
  constructor(
    private pageService: PageService,
    private apiBlockService: ApiBlockService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
  }

  load(page: Page, client: Client, lang: string, noCache = false): PageFrameBlocks {
    return {
      menu$: this.startEagerly(this.adapted(this.getMenu(page, client, lang, noCache))),
      footer$: this.startEagerly(this.adapted(this.getFooter(page.showFooter, lang, noCache))),
    };
  }

  private getMenu(page: Page, client: Client, lang: string, noCache: boolean): Observable<Block | null> {
    if (!page.showMenu || this.isServer) {
      return of(null);
    }
    const options: { clientId?: number; pageId?: number; menuBlockId?: number } = {};
    if (client?.id != null) {
      options.clientId = client.id;
    }
    if (page.id != null) {
      options.pageId = page.id;
    }
    if (page.menuBlockId != null) {
      options.menuBlockId = page.menuBlockId;
    }

    const cached = page.menuBlockId === null
      ? this.pageService.getDefaultMenu(lang)
      : this.pageService.getMenu(options.menuBlockId, lang);
    if (cached) {
      return of(cached);
    }

    return this.apiBlockService.getMenu(noCache, options).pipe(
      tap((menu) => menu && this.pageService.saveMenu(menu as SpecialBlock)),
      catchError(() => of(null)),
    );
  }

  private getFooter(showFooter: boolean, lang: string, noCache: boolean): Observable<Block | null> {
    if (!showFooter || this.isServer || this.pageService.isFooterMissing(lang)) {
      return of(null);
    }
    const cached = this.pageService.getFooter(lang);
    if (cached) {
      return of(cached);
    }

    return this.apiBlockService.getFooter(noCache).pipe(
      tap((footer) => footer && this.pageService.saveFooter(footer as SpecialBlock)),
      catchError((err) => {
        // Zapamiętujemy tylko jednoznaczny brak stopki; błąd sieci czy 500 mają prawo minąć.
        if (err instanceof HttpErrorResponse && err.status === 404) {
          this.pageService.markFooterMissing(lang);
        }
        return of(null);
      }),
    );
  }

  // Ten sam krok, który ClientPageAdapter robił dla menu i stopki, gdy leżały w blokach strony.
  private adapted(block$: Observable<Block | null>): Observable<Block | null> {
    return block$.pipe(map((block) => block ? createTypesRecursive([block])[0] : null));
  }

  // Żądanie musi wystartować od razu (tak jak wcześniej w resolverze), a nie dopiero wtedy,
  // gdy strona zasubskrybuje wynik po aktywacji trasy.
  private startEagerly<T>(source$: Observable<T>): Observable<T> {
    const result$ = new ReplaySubject<T>(1);
    source$.subscribe(result$);
    return result$.asObservable();
  }

  private get isServer(): boolean {
    return isPlatformServer(this.platformId);
  }
}
